import { type RouteLocationAsPathGeneric, useRouter } from "vue-router"
import { computed, type ComputedRef, type MaybeRefOrGetter, toValue } from "vue"
import { useCurrentStaticProviderKey } from "@versatile-credit-apply/sdk"

interface NavigationOptions {
  /**
   * Call router.replace() instead of router.push() when true
   */
  replace?: boolean
}

type UseApplyNavigation = (
  to?: Pick<RouteLocationAsPathGeneric, "query" | "force"> | undefined
) => ReturnType<
  ReturnType<typeof useRouter>["replace"] | ReturnType<typeof useRouter>["push"]
>

export const useApplyNavigationPath = (
  path: MaybeRefOrGetter<string>
): ComputedRef<string> => {
  const provider = useCurrentStaticProviderKey()
  return computed(() => `/apply/providers/${provider.value}/${toValue(path)}`)
}

/**
 * Navigate within the current provider to another route
 *
 * @param path the child path for the current provider
 * @param options navigation options that you desire
 */
export const useApplyNavigation = (
  path: MaybeRefOrGetter<string>,
  options?: NavigationOptions
): UseApplyNavigation => {
  const router = useRouter()
  const computedPath = useApplyNavigationPath(path)

  return (args) => {
    const to: RouteLocationAsPathGeneric = {
      path: toValue(computedPath),
    }

    if (args?.query) {
      to.query = args.query
    }

    return options?.replace ? router.replace(to) : router.push(to)
  }
}
